"use client";

import { useState } from "react";
import { CheckCircle2, Building2, Phone, Mail, Calendar, Users, ChevronRight, ShieldCheck } from "lucide-react";

const EVENT_TYPES = [
  "Music Festival", "Sporting Event", "Community Event", "Corporate Event",
  "Motorsport", "Cycling / Running", "Football / Rugby", "Horse Racing",
  "Airshow / Air Display", "Fair / Carnival", "Other",
];

const STEPS = [
  { id: 1, label: "Organisation" },
  { id: 2, label: "Events" },
  { id: 3, label: "Requirements" },
  { id: 4, label: "Submit" },
];

interface FormData {
  orgName: string; orgType: string; companyNumber: string;
  contactName: string; contactEmail: string; contactPhone: string;
  address: string; postcode: string;
  eventTypes: string[]; annualEvents: string; maxAttendance: string;
  existingProvider: string; contractLength: string;
  additionalInfo: string;
  agreeTerms: boolean; agreeGdpr: boolean;
}

const EMPTY: FormData = {
  orgName: "", orgType: "private", companyNumber: "",
  contactName: "", contactEmail: "", contactPhone: "",
  address: "", postcode: "",
  eventTypes: [], annualEvents: "", maxAttendance: "",
  existingProvider: "", contractLength: "1",
  additionalInfo: "",
  agreeTerms: false, agreeGdpr: false,
};

export default function CustomerRegisterPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [ref] = useState(`ENQ-2026-${String(Math.floor(Math.random() * 900) + 100)}`);

  function set<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleEventType(et: string) {
    setForm((f) => ({
      ...f,
      eventTypes: f.eventTypes.includes(et)
        ? f.eventTypes.filter((e) => e !== et)
        : [...f.eventTypes, et],
    }));
  }

  function handleSubmit() {
    setSubmitted(true);
  }

  const inp = (key: keyof FormData, type = "text", placeholder = "") => (
    <input
      type={type}
      value={form[key] as string}
      onChange={(e) => set(key, e.target.value as FormData[typeof key])}
      placeholder={placeholder}
      className="h-9 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  );

  const lbl = (text: string) => (
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">{text}</label>
  );

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto">
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm">
          <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Enquiry Submitted</h2>
          <p className="text-slate-500 mb-4">Thank you for your interest in Ockham Rescue&apos;s medical cover services.</p>
          <div className="inline-block bg-slate-100 border border-slate-200 rounded-xl px-5 py-3 mb-6">
            <p className="text-xs text-slate-500 mb-0.5">Your reference number</p>
            <p className="text-xl font-extrabold text-slate-900 font-mono">{ref}</p>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            A member of our team will be in touch within 2 working days to discuss your requirements and provide a tailored quote.
          </p>
          <div className="grid grid-cols-2 gap-3 text-xs text-slate-600">
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="font-semibold mb-0.5">Email</p>
              <p>events@ockhamrescue.co.uk</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="font-semibold mb-0.5">Phone</p>
              <p>01483 000000</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-extrabold text-slate-900">Register Your Organisation</h1>
        <p className="text-sm text-slate-500 mt-1">Tell us about your organisation and event medical cover requirements.</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-8">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all ${
              step === s.id ? "bg-blue-600 text-white" : step > s.id ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
            }`}>
              {step > s.id ? <CheckCircle2 className="h-4 w-4" /> : s.id}
            </div>
            <span className={`text-xs ml-2 font-medium ${step === s.id ? "text-slate-900" : "text-slate-400"}`}>{s.label}</span>
            {i < STEPS.length - 1 && <div className={`h-px w-8 mx-3 ${step > s.id ? "bg-emerald-300" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">

        {/* Step 1: Organisation */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-5">
              <Building2 className="h-4 w-4 text-blue-600" />
              <h2 className="font-bold text-slate-900">Organisation Details</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                {lbl("Organisation / Company Name *")}
                {inp("orgName", "text", "e.g. Guildford Events Ltd")}
              </div>
              <div>
                {lbl("Organisation Type")}
                <select value={form.orgType} onChange={(e) => set("orgType", e.target.value)} className="h-9 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="private">Private Company</option>
                  <option value="charity">Charity / CIC</option>
                  <option value="local_authority">Local Authority</option>
                  <option value="motorsport">Motorsport / Race</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                {lbl("Company / Charity Number")}
                {inp("companyNumber", "text", "e.g. 12345678")}
              </div>
              <div className="col-span-2 border-t border-slate-100 pt-4">
                {lbl("Primary Contact Name *")}
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input type="text" value={form.contactName} onChange={(e) => set("contactName", e.target.value)} placeholder="Full name" className="h-9 w-full rounded-md border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                {lbl("Email Address *")}
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input type="email" value={form.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} placeholder="contact@company.com" className="h-9 w-full rounded-md border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                {lbl("Phone Number *")}
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input type="tel" value={form.contactPhone} onChange={(e) => set("contactPhone", e.target.value)} placeholder="01xxx xxxxxx" className="h-9 w-full rounded-md border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="col-span-2">
                {lbl("Registered Address")}
                {inp("address", "text", "Street, Town, County")}
              </div>
              <div>
                {lbl("Postcode")}
                {inp("postcode", "text", "e.g. GU1 1AB")}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Event Types */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-5">
              <Calendar className="h-4 w-4 text-blue-600" />
              <h2 className="font-bold text-slate-900">Event Types</h2>
            </div>
            <div>
              {lbl("What types of events do you run? (select all that apply)")}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {EVENT_TYPES.map((et) => (
                  <button
                    key={et}
                    type="button"
                    onClick={() => toggleEventType(et)}
                    className={`text-left text-xs px-3 py-2 rounded-lg border transition-all font-medium ${
                      form.eventTypes.includes(et)
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:border-blue-300"
                    }`}
                  >
                    {et}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div>
                {lbl("Approximate events per year")}
                <select value={form.annualEvents} onChange={(e) => set("annualEvents", e.target.value)} className="h-9 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Select…</option>
                  <option value="1-3">1–3 events</option>
                  <option value="4-10">4–10 events</option>
                  <option value="11-25">11–25 events</option>
                  <option value="25+">25+ events</option>
                </select>
              </div>
              <div>
                {lbl("Maximum expected attendance (single event)")}
                <select value={form.maxAttendance} onChange={(e) => set("maxAttendance", e.target.value)} className="h-9 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">Select…</option>
                  <option value="under500">Under 500</option>
                  <option value="500-2000">500–2,000</option>
                  <option value="2000-10000">2,000–10,000</option>
                  <option value="10000-50000">10,000–50,000</option>
                  <option value="50000+">50,000+</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Requirements */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-5">
              <ShieldCheck className="h-4 w-4 text-blue-600" />
              <h2 className="font-bold text-slate-900">Medical Cover Requirements</h2>
            </div>
            <div>
              {lbl("Do you currently use a medical cover provider?")}
              <select value={form.existingProvider} onChange={(e) => set("existingProvider", e.target.value)} className="h-9 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Prefer not to say</option>
                <option value="none">No — this is our first time</option>
                <option value="yes">Yes — looking to switch providers</option>
                <option value="tender">Yes — running a tender process</option>
              </select>
            </div>
            <div>
              {lbl("Preferred contract length")}
              <select value={form.contractLength} onChange={(e) => set("contractLength", e.target.value)} className="h-9 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="1">Single event / ad hoc</option>
                <option value="season">Season / annual</option>
                <option value="2">2-year contract</option>
                <option value="3+">3+ years</option>
              </select>
            </div>
            <div>
              {lbl("Additional information or specific requirements")}
              <textarea
                value={form.additionalInfo}
                onChange={(e) => set("additionalInfo", e.target.value)}
                placeholder="Tell us about any specific clinical requirements, unusual venues, high-risk elements, existing safety documentation, etc."
                rows={5}
                className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>
        )}

        {/* Step 4: Declaration */}
        {step === 4 && (
          <div className="space-y-5">
            <h2 className="font-bold text-slate-900 mb-4">Review &amp; Submit</h2>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Organisation</span><span className="font-semibold">{form.orgName || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Contact</span><span className="font-semibold">{form.contactName || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Email</span><span>{form.contactEmail || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Phone</span><span>{form.contactPhone || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Events per year</span><span>{form.annualEvents || "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Max attendance</span><span>{form.maxAttendance || "—"}</span></div>
              <div className="flex justify-between items-start gap-4"><span className="text-slate-500 shrink-0">Event types</span><span className="text-right">{form.eventTypes.join(", ") || "—"}</span></div>
            </div>

            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.agreeTerms} onChange={(e) => set("agreeTerms", e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-slate-700">
                  I confirm the information provided is accurate and I am authorised to submit this enquiry on behalf of my organisation. I agree to Ockham Rescue&apos;s <a href="#" className="text-blue-600 underline">Terms &amp; Conditions</a>.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.agreeGdpr} onChange={(e) => set("agreeGdpr", e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-slate-700">
                  I consent to my data being processed by Ockham Rescue Ltd for the purpose of responding to this enquiry, in accordance with the UK GDPR and our <a href="#" className="text-blue-600 underline">Privacy Policy</a>.
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6 pt-4 border-t border-slate-100">
          {step > 1 ? (
            <button onClick={() => setStep((s) => s - 1)} className="text-sm text-slate-500 hover:text-slate-700 font-medium">← Back</button>
          ) : <div />}

          {step < 4 ? (
            <button
              onClick={() => setStep((s) => s + 1)}
              className="flex items-center gap-2 bg-blue-600 text-white rounded-xl px-5 py-2 text-sm font-bold hover:bg-blue-700 transition-colors"
            >
              Continue <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!form.agreeTerms || !form.agreeGdpr}
              className="flex items-center gap-2 bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2 text-sm font-bold hover:bg-blue-700 transition-colors"
            >
              <CheckCircle2 className="h-4 w-4" />Submit Enquiry
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-center text-slate-400 mt-6">
        Already have an account? <a href="/portal/login" className="text-blue-600 hover:underline">Sign in to your portal</a>
      </p>
    </div>
  );
}
